// src/pages/SignUp.jsx
// Create/Join clinic flow with:
// - "Back to login" button
// - Anonymous sign-in before calling providerSignUp (fixes functions/unauthenticated)
// - Duplicate email guard
// - Redirect to /login?clinic=CODE on success

import * as React from "react";
import {
  Box, Container, Paper, Stack, Button, TextField,
  Switch, FormControlLabel, Alert, Typography
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { auth, db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { fetchSignInMethodsForEmail, signInAnonymously } from "firebase/auth";

function normCode(raw) {
  return String(raw || "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function genCode(len = 7) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let s = "";
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}
function humanize(err) {
  const d = err?.customData || err?.details || {};
  return [
    err?.message, err?.code, d?.message, d?.error, typeof d === "string" ? d : ""
  ].filter(Boolean).join(" | ") || "Unknown error";
}

export default function SignUp() {
  const nav = useNavigate();

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  const [createNew, setCreateNew] = React.useState(true);
  const [clinicName, setClinicName] = React.useState("");
  const [clinicCode, setClinicCode] = React.useState(genCode());
  const [codeExists, setCodeExists] = React.useState(null); // null|true|false
  const [resolvedName, setResolvedName] = React.useState(""); // when joining

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");

  // Live existence check + name preview for the clinic code
  React.useEffect(() => {
    const code = normCode(clinicCode);
    if (!code) { setCodeExists(null); setResolvedName(""); return; }
    (async () => {
      try {
        const snap = await getDoc(doc(db, "clinics", code));
        const exists = snap.exists();
        setCodeExists(exists);
        setResolvedName(exists ? (snap.data()?.name || code) : "");
      } catch {
        setCodeExists(null);
        setResolvedName("");
      }
    })();
  }, [clinicCode, createNew]);

  async function onSubmit(e) {
    e.preventDefault();
    setError(""); setInfo(""); setSubmitting(true);
    try {
      const code = normCode(clinicCode);
      if (!code) throw new Error("Please enter a clinic code.");
      if (createNew && !clinicName.trim()) throw new Error("Please enter a clinic name.");
      if (createNew && codeExists === true) throw new Error("That clinic code already exists. Generate a new one or choose another.");
      if (!createNew && codeExists === false) throw new Error("We couldn't find that clinic code. Double-check with your admin.");

      // Block duplicate accounts (if email already has password sign-in, don't allow re-create)
      const methods = await fetchSignInMethodsForEmail(auth, email.trim());
      if (createNew && methods && methods.includes("password")) {
        throw new Error("An account already exists for this email. Please log in instead.");
      }

      // Ensure the callable has context.auth — requires Anonymous provider enabled
      try {
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
      } catch (anonErr) {
        // If anonymous sign-in isn't enabled, show a clear message
        if (String(anonErr?.code).includes("operation-not-allowed")) {
          throw new Error(
            "Anonymous sign-in is disabled. Enable it in Firebase Auth → Sign-in method so signup can proceed."
          );
        }
        throw anonErr;
      }

      // Call your backend to create/join
      const signUp = httpsCallable(functions, "providerSignUp");
      const res = await signUp({
        email: email.trim(),
        password,
        createNew,
        clinicName: createNew ? clinicName.trim() : undefined,
        clinicCode: code,
      });

      if (res?.data?.ok) {
        setInfo(createNew ? "Clinic created! Redirecting to login…" : "Joined! Redirecting to login…");
        setTimeout(() => nav(`/login?clinic=${encodeURIComponent(code)}`), 600);
      } else {
        throw new Error(res?.data?.error || "Unknown signup error");
      }
    } catch (err) {
      setError(humanize(err));
    } finally {
      setSubmitting(false);
    }
  }

  const joining = !createNew;
  const helper =
    joining
      ? (resolvedName ? `Clinic: ${resolvedName}`
        : codeExists === false ? "We couldn't find this clinic yet."
        : "Paste the code from your admin.")
      : (codeExists === true ? "This code is already taken."
        : codeExists === false ? "Code looks available."
        : "Enter a code or generate a new one.");

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3, bgcolor: "#f7f9fc" }}>
      <Container maxWidth="sm">
        <Paper elevation={1} sx={{ p: 3, borderRadius: 4 }}>
          {/* Back to Login */}
          <div className="mb-3 flex items-center justify-between">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {joining ? "Join Clinic" : "Create Clinic"}
            </Typography>
            <Button component={RouterLink} to="/login" variant="text" size="small">
              Back to login
            </Button>
          </div>

          {error && <Alert severity="error" sx={{ mb: 2, wordBreak: "break-word" }}>{error}</Alert>}
          {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

          <form onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Work Email *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <TextField
                label="Password *"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />

              <FormControlLabel
                control={<Switch checked={createNew} onChange={(e) => setCreateNew(e.target.checked)} />}
                label="Create a new clinic"
              />

              {createNew && (
                <TextField
                  label="Clinic Name *"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  required
                />
              )}

              <TextField
                label="Clinic Code *"
                value={clinicCode}
                onChange={(e) => setClinicCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                inputProps={{ maxLength: 10 }}
                required
                helperText={helper}
              />

              {createNew && (
                <Button variant="outlined" onClick={() => setClinicCode(genCode())}>
                  Generate
                </Button>
              )}

              <Button type="submit" variant="contained" disabled={submitting}>
                {createNew ? "Create Clinic & Continue" : "Join Clinic & Continue"}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
